import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../contexts/AuthContext';
import type { OffsetPaginatedResponse, PaginationInfo } from '../types';
import Pagination from '../components/ui/pagination';
import { Tabs } from '../components/ui/tabs';
import { SearchInput } from '../components/ui/SearchInput';
import { FilterSelect } from '../components/ui/FilterSelect';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SegmentedSwitch from '../components/scheduledJobs/SegmentedSwitch';
import { usePortalSlot } from '../hooks/usePortalSlot';
import { TableFillBox, fillOrHugClass, tableFillHeightClass } from '../components/ui/tableFillBox';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from '../components/ui/table';
import { ActionIconButton } from '../components/ui/ActionIconButton';
import { StatCard } from '../components/ui/StatCard';
import {
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// App-standard pill input — fully rounded to match SearchInput's pill chrome,
// per the locked Create/Edit Site List modal spec (deepcontext-gncj).
const PILL_INPUT = 'w-full rounded-full border border-line bg-surface px-4 py-2 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all';

// Pill select for the filter row — the search input and both selects sit on
// one aligned row (deepcontext-jlkv).
const PILL_SELECT = 'rounded-full border border-line bg-surface px-3.5 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all';

// Monospace domain textarea — a box, not a pill (bg-transparent + rounded-xl),
// matching the locked BlueprintFormModal prompt textarea.
const DOMAIN_TEXTAREA = 'w-full rounded-xl border border-line bg-transparent px-3.5 py-3 text-sm font-mono text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all';

// Types matching backend
interface SiteList {
  id: string;
  name: string;
  description?: string;
  listType: 'include' | 'exclude';
  domains: string[];
  companyId: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface FlattenedDomain {
  domain: string;
  listId: string;
  listName: string;
  listType: 'include' | 'exclude';
}


const MAX_DOMAINS = 200;

const SiteLists: React.FC = () => {
  // Segmented Domains/Manage switch + Refresh/Create actions portal onto the
  // shared app-page-actions row (deepcontext-gncj) — same convention as
  // UserManagement/QueueAdmin — instead of a local underline-tab header.
  const actionsSlot = usePortalSlot('app-page-actions');

  // Tab state
  const [activeTab, setActiveTab] = useState<'domains' | 'lists'>('domains');

  // Domains tab state
  const [domains, setDomains] = useState<FlattenedDomain[]>([]);
  const [domainsTotal, setDomainsTotal] = useState(0);
  const [domainsTotalPages, setDomainsTotalPages] = useState(1);
  const [domainsPage, setDomainsPage] = useState(1);
  const [domainsPageSize, setDomainsPageSize] = useState(25);
  const [domainsSearch, setDomainsSearch] = useState('');
  const [domainsFilterListId, setDomainsFilterListId] = useState<string>('');
  const [domainsFilterType, setDomainsFilterType] = useState<string>('');
  const [domainsLoading, setDomainsLoading] = useState(false);

  // Lists tab state
  const [lists, setLists] = useState<SiteList[]>([]);
  const [listsTotal, setListsTotal] = useState(0);
  const [listsTotalPages, setListsTotalPages] = useState(1);
  const [listsPage, setListsPage] = useState(1);
  const [listsPageSize, setListsPageSize] = useState(10);
  const [listsSearch, setListsSearch] = useState('');
  const [listsLoading, setListsLoading] = useState(false);

  // For dropdown filter (need all lists for the dropdown)
  const [allLists, setAllLists] = useState<SiteList[]>([]);

  // General state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingList, setEditingList] = useState<SiteList | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    listType: 'include' as 'include' | 'exclude',
    domainsText: '',
  });
  const [parsedDomains, setParsedDomains] = useState<string[]>([]);

  // Stats (calculated from totals)
  const includeLists = allLists.filter((l) => l.listType === 'include').length;
  const excludeLists = allLists.filter((l) => l.listType === 'exclude').length;

  // Load all lists for dropdown and stats (lightweight call)
  const loadAllLists = useCallback(async () => {
    try {
      const response = await api.get('/api/site-lists');
      setAllLists(response.data);
    } catch (err: any) {
      console.error('Failed to load all lists:', err);
    }
  }, []);

  // Load domains with pagination
  const loadDomains = useCallback(async () => {
    try {
      setDomainsLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: domainsPage.toString(),
        pageSize: domainsPageSize.toString(),
      });

      if (domainsSearch) params.append('search', domainsSearch);
      if (domainsFilterListId) params.append('listId', domainsFilterListId);
      if (domainsFilterType) params.append('type', domainsFilterType);

      const response = await api.get<OffsetPaginatedResponse<FlattenedDomain>>(
        `/api/site-lists/domains?${params.toString()}`
      );

      setDomains(response.data.data);
      setDomainsTotal(response.data.total);
      setDomainsTotalPages(response.data.totalPages);
    } catch (err: any) {
      console.error('Failed to load domains:', err);
      setError(err.response?.data?.error || 'Failed to load domains');
    } finally {
      setDomainsLoading(false);
    }
  }, [domainsPage, domainsPageSize, domainsSearch, domainsFilterListId, domainsFilterType]);

  // Load lists with pagination
  const loadLists = useCallback(async () => {
    try {
      setListsLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: listsPage.toString(),
        pageSize: listsPageSize.toString(),
      });

      if (listsSearch) params.append('search', listsSearch);

      const response = await api.get<OffsetPaginatedResponse<SiteList>>(
        `/api/site-lists/paginated?${params.toString()}`
      );

      setLists(response.data.data);
      setListsTotal(response.data.total);
      setListsTotalPages(response.data.totalPages);
    } catch (err: any) {
      console.error('Failed to load lists:', err);
      setError(err.response?.data?.error || 'Failed to load lists');
    } finally {
      setListsLoading(false);
    }
  }, [listsPage, listsPageSize, listsSearch]);

  // Initial load
  useEffect(() => {
    loadAllLists();
  }, [loadAllLists]);

  // Load data when tab changes or filters change
  useEffect(() => {
    if (activeTab === 'domains') {
      loadDomains();
    } else {
      loadLists();
    }
  }, [activeTab, loadDomains, loadLists]);

  // Reset page when filters change
  useEffect(() => {
    setDomainsPage(1);
  }, [domainsSearch, domainsFilterListId, domainsFilterType]);

  useEffect(() => {
    setListsPage(1);
  }, [listsSearch]);

  // Parse domains when text changes
  useEffect(() => {
    const domains = parseDomainText(formData.domainsText);
    setParsedDomains(domains);
  }, [formData.domainsText]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const parseDomainText = (text: string): string[] => {
    if (!text.trim()) return [];

    const lines = text.split(/[\n,;]+/);
    const domains: string[] = [];
    const seen = new Set<string>();

    for (const line of lines) {
      let domain = line.trim();
      if (!domain) continue;

      domain = domain.replace(/^https?:\/\//, '');
      domain = domain.replace(/^www\./, '');
      domain = domain.split('/')[0].split('?')[0].split('#')[0];
      domain = domain.split(':')[0];
      domain = domain.toLowerCase();

      if (domain && domain.includes('.') && /^[a-z0-9.-]+$/.test(domain)) {
        if (!seen.has(domain)) {
          seen.add(domain);
          domains.push(domain);
        }
      }
    }

    return domains;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      listType: 'include',
      domainsText: '',
    });
    setParsedDomains([]);
    setEditingList(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (list: SiteList) => {
    setEditingList(list);
    setFormData({
      name: list.name,
      description: list.description || '',
      listType: list.listType,
      domainsText: list.domains.join('\n'),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('List name is required');
      return;
    }

    if (parsedDomains.length === 0) {
      setError('At least one valid domain is required');
      return;
    }

    if (parsedDomains.length > MAX_DOMAINS) {
      setError(`Maximum ${MAX_DOMAINS} domains allowed`);
      return;
    }

    try {
      setError(null);

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        listType: formData.listType,
        domains: parsedDomains,
      };

      if (editingList) {
        await api.put(`/api/site-lists/${editingList.id}`, payload);
        setSuccess('Site list updated successfully');
      } else {
        await api.post('/api/site-lists', payload);
        setSuccess('Site list created successfully');
      }

      setShowModal(false);
      resetForm();

      // Reload data
      loadAllLists();
      if (activeTab === 'domains') {
        loadDomains();
      } else {
        loadLists();
      }
    } catch (err: any) {
      console.error('Failed to save site list:', err);
      setError(err.response?.data?.error || 'Failed to save site list');
    }
  };

  const handleDeleteList = async (list: SiteList) => {
    if (!window.confirm(`Are you sure you want to delete "${list.name}" with ${list.domains.length} domains?`)) {
      return;
    }

    try {
      setError(null);
      await api.delete(`/api/site-lists/${list.id}`);
      setSuccess('Site list deleted successfully');

      // Reload data
      loadAllLists();
      if (activeTab === 'domains') {
        loadDomains();
      } else {
        loadLists();
      }
    } catch (err: any) {
      console.error('Failed to delete site list:', err);
      setError(err.response?.data?.error || 'Failed to delete site list');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // PaginationInfo objects for the shared pagerow (deepcontext-pd8c) — one
  // component, all pages inherit the same pill/gradient/40%-disabled styling.
  const domainsPaginationInfo: PaginationInfo = {
    page: domainsPage,
    limit: domainsPageSize,
    totalCount: domainsTotal,
    totalPages: domainsTotalPages,
    hasNextPage: domainsPage < domainsTotalPages,
    hasPreviousPage: domainsPage > 1,
    nextPage: domainsPage < domainsTotalPages ? domainsPage + 1 : null,
    previousPage: domainsPage > 1 ? domainsPage - 1 : null,
  };

  const listsPaginationInfo: PaginationInfo = {
    page: listsPage,
    limit: listsPageSize,
    totalCount: listsTotal,
    totalPages: listsTotalPages,
    hasNextPage: listsPage < listsTotalPages,
    hasPreviousPage: listsPage > 1,
    nextPage: listsPage < listsTotalPages ? listsPage + 1 : null,
    previousPage: listsPage > 1 ? listsPage - 1 : null,
  };

  // Table-fill contract (bd-n3uyh, shared TableFillBox — deepcontext-wbra):
  // a full page of the current tab's results (total >= page size) fills the
  // table box down to the pagination and scrolls internally; a sparse page
  // hugs its content. Same rule for both tabs, keyed off each tab's own
  // total, mirroring UserManagement.tsx/ScheduledJobsPage.tsx.
  const isDomainsFullPage = domainsTotal >= domainsPageSize;
  const isListsFullPage = listsTotal >= listsPageSize;

  return (
    <div className="flex flex-col h-full min-h-0 gap-6">
      {/* Segmented Domains/Manage switch + Refresh/Create, portaled onto
          AppLayout's shared actions row. */}
      {actionsSlot && createPortal(
        <>
          <Tabs
            tabs={[
              { value: 'domains', label: `Domains · ${domainsTotal.toLocaleString()}` },
              // allLists is fetched unconditionally on mount (loadAllLists),
              // unlike listsTotal which only populates once the Lists tab has
              // been visited — using it keeps this badge live from first paint.
              { value: 'lists', label: `Manage Site Lists · ${allLists.length.toLocaleString()}` },
            ]}
            value={activeTab}
            onChange={(value) => setActiveTab(value as 'domains' | 'lists')}
          />
          <Button
            variant="ghost"
            onClick={() => {
              loadAllLists();
              if (activeTab === 'domains') loadDomains();
              else loadLists();
            }}
            disabled={domainsLoading || listsLoading}
          >
            <ArrowPathIcon className={`h-4 w-4 ${domainsLoading || listsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openCreateModal}>
            Create Site List
          </Button>
        </>,
        actionsSlot
      )}

      {/* KPI card row — shared StatCard components (Style C: no icons).
          Include Lists value color is safe token; Exclude Lists is risk token. */}
      <div className="flex-shrink-0 grid grid-cols-4 gap-4">
        <StatCard
          testId="kpi-total-lists"
          label="Total Lists"
          value={allLists.length}
        />
        <StatCard
          testId="kpi-total-domains"
          label="Total Domains"
          value={domainsTotal.toLocaleString()}
        />
        <StatCard
          testId="kpi-include-lists"
          label="Include Lists"
          value={includeLists}
          valueColorClassName="text-safe"
        />
        <StatCard
          testId="kpi-exclude-lists"
          label="Exclude Lists"
          value={excludeLists}
          valueColorClassName="text-risk"
        />
      </div>

      {/* Messages */}
      {error && (
        <div className="flex-shrink-0 bg-risk-bg border border-risk rounded-lg p-4">
          <div className="flex items-center gap-2">
            <XCircleIcon className="h-5 w-5 text-risk" />
            <span className="text-risk">{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="flex-shrink-0 bg-safe-bg border border-safe rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-safe" />
            <span className="text-safe">{success}</span>
          </div>
        </div>
      )}

      {/* Search and Filters — standalone row above the table card, mirroring
          Brand Blueprints' SearchFilterBar pattern (deepcontext-nzhl,
          supersedes the deepcontext-jlkv in-card ghost-bg strip). */}
      {activeTab === 'domains' && (
        <div className="flex-shrink-0 flex flex-wrap items-center gap-3">
          <SearchInput
            placeholder="Search domains or list names..."
            value={domainsSearch}
            onChange={(e) => setDomainsSearch(e.target.value)}
            className="flex-1 min-w-64"
          />

          <FilterSelect
            data-testid="domains-filter-list"
            value={domainsFilterListId}
            onChange={setDomainsFilterListId}
            options={[
              { value: '', label: 'All Lists' },
              ...allLists.map((list) => ({
                value: list.id,
                label: `${list.name} (${list.domains.length})`,
              })),
            ]}
          />

          <FilterSelect
            data-testid="domains-filter-type"
            value={domainsFilterType}
            onChange={setDomainsFilterType}
            options={[
              { value: '', label: 'All Types' },
              { value: 'include', label: 'Include Only' },
              { value: 'exclude', label: 'Exclude Only' },
            ]}
          />

          <div className="flex items-center gap-2">
            <span className="text-sm text-ink-muted">Show:</span>
            <FilterSelect
              data-testid="domains-page-size"
              value={String(domainsPageSize)}
              onChange={(value) => {
                setDomainsPageSize(Number(value));
                setDomainsPage(1);
              }}
              options={[
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
              ]}
            />
          </div>
        </div>
      )}

      {activeTab === 'lists' && (
        <div className="flex-shrink-0 flex items-center gap-3">
          <SearchInput
            placeholder="Search lists by name or description..."
            value={listsSearch}
            onChange={(e) => setListsSearch(e.target.value)}
            className="flex-1 max-w-md"
          />
          <div className="flex items-center gap-2">
            <span className="text-sm text-ink-muted">Show:</span>
            <select
              value={listsPageSize}
              onChange={(e) => {
                setListsPageSize(Number(e.target.value));
                setListsPage(1);
              }}
              className={PILL_SELECT}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      )}

      {/* Tab content — the card fills remaining page height so its active
          tab's table box can reach the table-fill contract below
          (bd-n3uyh, deepcontext-wbra). */}
      <div className="flex-1 min-h-0 flex flex-col bg-surface rounded-2xl border border-line shadow-sm overflow-hidden">
        {/* Domains Tab Content — the table-fill contract (bd-n3uyh,
            deepcontext-wbra) is keyed off isDomainsFullPage at both this
            level and the table box below, mirroring BlueprintsTable.tsx. */}
        {activeTab === 'domains' && (
          <div className={`${fillOrHugClass(isDomainsFullPage)} min-h-0 flex flex-col`}>
            {/* Domains Table */}
            <TableFillBox isFullPage={isDomainsFullPage} data-testid="sitelists-domains-table-fill-box">
              {domainsLoading && domains.length === 0 ? (
                <div className="p-8 text-center">
                  <ArrowPathIcon className="h-8 w-8 animate-spin text-ink-muted mx-auto mb-2" />
                  <p className="text-ink-muted">Loading...</p>
                </div>
              ) : domains.length === 0 ? (
                <div className="p-8 text-center text-ink-muted">
                  {domainsSearch || domainsFilterListId || domainsFilterType ? (
                    <p>No domains match your filters.</p>
                  ) : (
                    <div>
                      <ClipboardDocumentListIcon className="h-12 w-12 text-ink-muted mx-auto mb-3" />
                      <p>No domains found. Create a site list to get started.</p>
                    </div>
                  )}
                </div>
              ) : (
                <Table className={tableFillHeightClass(isDomainsFullPage)}>
                  <TableHead>
                    <tr>
                      <TableHeaderCell>Domain</TableHeaderCell>
                      <TableHeaderCell>Site List</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                    </tr>
                  </TableHead>
                  <TableBody>
                    {domains.map((item, idx) => (
                      <TableRow key={`${item.listId}-${item.domain}-${idx}`}>
                        <TableCell className="align-middle font-mono">{item.domain}</TableCell>
                        <TableCell className="align-middle text-ink-muted">{item.listName}</TableCell>
                        <TableCell className="align-middle">
                          <Badge variant={item.listType === 'include' ? 'safe' : 'risk'}>
                            {item.listType}
                          </Badge>
                        </TableCell>
                        <TableCell className="align-middle text-right">
                          <button
                            onClick={() => {
                              const list = allLists.find((l) => l.id === item.listId);
                              if (list) openEditModal(list);
                            }}
                            className="text-accent hover:text-accent-active text-sm font-medium"
                          >
                            Edit List
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableFillBox>

            {domainsTotalPages > 1 && (
              <div className="flex-shrink-0 border-t border-line bg-ghost-bg px-4 py-2.5" data-testid="sitelists-domains-pagination">
                <Pagination
                  pagination={domainsPaginationInfo}
                  onPageChange={setDomainsPage}
                />
              </div>
            )}
          </div>
        )}

        {/* Manage Site Lists Tab Content — same table-fill contract as the
            Domains tab above (bd-n3uyh, deepcontext-wbra). */}
        {activeTab === 'lists' && (
          <div className={`${fillOrHugClass(isListsFullPage)} min-h-0 flex flex-col`}>
            {/* Lists Table */}
            <TableFillBox isFullPage={isListsFullPage} data-testid="sitelists-lists-table-fill-box">
            {listsLoading && lists.length === 0 ? (
              <div className="p-8 text-center">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-ink-muted mx-auto mb-2" />
                <p className="text-ink-muted">Loading...</p>
              </div>
            ) : lists.length === 0 ? (
              <div className="p-8 text-center text-ink-muted">
                {listsSearch ? (
                  <p>No lists match your search.</p>
                ) : (
                  <div>
                    <ClipboardDocumentListIcon className="h-12 w-12 text-ink-muted mx-auto mb-3" />
                    <p className="mb-4">No site lists created yet.</p>
                    <button
                      onClick={openCreateModal}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-active"
                    >
                      Create Your First Site List
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Table className={tableFillHeightClass(isListsFullPage)}>
                <TableHead>
                  <tr>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Description</TableHeaderCell>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell className="text-right">Domains</TableHeaderCell>
                    <TableHeaderCell>Created</TableHeaderCell>
                    <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                  </tr>
                </TableHead>
                <TableBody>
                  {lists.map((list) => (
                    <TableRow key={list.id}>
                      <TableCell className="align-middle">
                        <span className="font-medium text-accent-active">{list.name}</span>
                      </TableCell>
                      <TableCell className="align-middle text-ink-muted max-w-xs truncate">
                        {list.description || '-'}
                      </TableCell>
                      <TableCell className="align-middle">
                        <Badge variant={list.listType === 'include' ? 'safe' : 'risk'}>
                          {list.listType}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="align-middle text-ink-muted text-right tabular-nums"
                        data-testid={`sitelists-list-domains-${list.id}`}
                      >
                        {list.domains.length.toLocaleString()}
                      </TableCell>
                      <TableCell className="align-middle text-ink-muted">{formatDate(list.createdAt)}</TableCell>
                      <TableCell className="align-middle text-right">
                        <div className="flex justify-end gap-1">
                          <ActionIconButton
                            kind="ghost"
                            onClick={() => openEditModal(list)}
                            title="Edit"
                            data-testid={`sitelists-edit-${list.id}`}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </ActionIconButton>
                          <ActionIconButton
                            kind="delete"
                            onClick={() => handleDeleteList(list)}
                            title="Delete"
                            data-testid={`sitelists-delete-${list.id}`}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </ActionIconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            </TableFillBox>

            {listsTotalPages > 1 && (
              <div className="flex-shrink-0 border-t border-line bg-ghost-bg px-4 py-2.5" data-testid="sitelists-lists-pagination">
                <Pagination
                  pagination={listsPaginationInfo}
                  onPageChange={setListsPage}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal — tokened pill inputs, a segmented List Type
          switch (not native radios), and a ghost Cancel + gradient
          Update/Create, per the locked modal spec (deepcontext-gncj). */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-2xl border border-line shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-line">
              <h3 className="text-lg font-semibold text-ink">
                {editingList ? 'Edit Site List' : 'Create Site List'}
              </h3>
            </div>

            <div className="p-4 flex-1 overflow-y-auto space-y-4">
              {error && (
                <div className="p-3 bg-risk-bg border border-risk rounded-lg flex items-center gap-2 text-risk">
                  <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-ink mb-1">List Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Vietnam News Sites"
                  className={PILL_INPUT}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this list"
                  className={PILL_INPUT}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-2">List Type</label>
                <SegmentedSwitch
                  label="List Type"
                  value={formData.listType}
                  onChange={(value) => setFormData({ ...formData, listType: value })}
                  options={[
                    { value: 'include', label: 'Include' },
                    { value: 'exclude', label: 'Exclude' },
                  ]}
                />
                <p className="mt-1.5 text-xs text-ink-muted">
                  {formData.listType === 'include'
                    ? 'Only scan these domains'
                    : 'Skip these domains'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1">Domains *</label>
                <textarea
                  value={formData.domainsText}
                  onChange={(e) => setFormData({ ...formData, domainsText: e.target.value })}
                  placeholder={`Paste domains here, one per line or comma-separated:\nexample.com\nhttps://www.news-site.com/article\nother-domain.org`}
                  className={DOMAIN_TEXTAREA}
                  rows={8}
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-ink-muted">
                    URLs, protocols, paths, and www will be automatically cleaned
                  </p>
                  <p
                    className={`text-xs ${
                      parsedDomains.length > MAX_DOMAINS ? 'text-risk' : 'text-ink-muted'
                    }`}
                  >
                    {parsedDomains.length} / {MAX_DOMAINS} domains
                  </p>
                </div>
              </div>

              {parsedDomains.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-ink mb-1">Preview</p>
                  <div className="max-h-32 overflow-y-auto p-3 bg-canvas rounded-xl">
                    <div className="flex flex-wrap gap-1.5">
                      {parsedDomains.slice(0, 50).map((domain, index) => (
                        <span
                          key={index}
                          className="px-2.5 py-1 bg-surface border border-line rounded-full text-xs font-mono text-ink"
                        >
                          {domain}
                        </span>
                      ))}
                      {parsedDomains.length > 50 && (
                        <span className="px-2 py-0.5 text-xs text-ink-muted">
                          +{parsedDomains.length - 50} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-line flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={
                  parsedDomains.length === 0 ||
                  parsedDomains.length > MAX_DOMAINS ||
                  !formData.name.trim()
                }
              >
                {editingList ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteLists;
